import type { OptionFetcher } from '@eo/shared';

import getApiHttpClient from '../../../httpClients/getApiHttpClient';
import type {
  CapacityClientRow,
  CapacityEntryRow,
  CapacityReport,
  ClientListParams,
  ClientOption,
  ClientTaskOption,
  EntryListParams,
  ExcessBill,
  ExcessExportBody,
  ExcessExportRequested,
  GenerateExcessBillBody,
  PaginationMeta,
  PersonOption,
  SyncResult,
} from './types';

export async function fetchReport(month: string, owner?: string): Promise<CapacityReport> {
  const res = await getApiHttpClient().get<CapacityReport>('/api/v1/cfo/capacity/report', {
    params: { month, owner },
  });
  return res.data;
}

export async function fetchClientRows(
  month: string,
  owner: string | undefined,
  params: ClientListParams,
): Promise<{ clients: CapacityClientRow[]; pagination: PaginationMeta }> {
  const res = await getApiHttpClient().get<{
    clients: CapacityClientRow[];
    pagination: PaginationMeta;
  }>('/api/v1/cfo/capacity/clients', { params: { month, owner, ...params } });
  return res.data;
}

export async function fetchEntryRows(
  month: string,
  owner: string | undefined,
  params: EntryListParams,
): Promise<{ entries: CapacityEntryRow[]; pagination: PaginationMeta }> {
  const res = await getApiHttpClient().get<{
    entries: CapacityEntryRow[];
    pagination: PaginationMeta;
  }>('/api/v1/cfo/capacity/entries', { params: { month, owner, ...params } });
  return res.data;
}

export async function syncMonth(month: string): Promise<{ results: SyncResult[] }> {
  const res = await getApiHttpClient().post<{ results: SyncResult[] }>(
    '/api/v1/cfo/capacity/sync',
    { month },
  );
  return res.data;
}

export async function setClientHours(body: {
  clientId: string;
  effectiveMonth: string;
  fixedHours: number;
  variableHours: number;
}): Promise<void> {
  await getApiHttpClient().put('/api/v1/cfo/capacity/budgets', body);
}

export async function shareClient(body: {
  clientId: string;
  userId: string;
  fixedHours: number;
  variableHours: number;
}): Promise<void> {
  await getApiHttpClient().put('/api/v1/cfo/capacity/assignments', body);
}

export async function unshareClient(assignmentId: string): Promise<void> {
  await getApiHttpClient().delete(`/api/v1/cfo/capacity/assignments/${assignmentId}`);
}

export async function addExtraHours(body: {
  clientId: string;
  bucket: 'fixed' | 'variable';
  hours: number;
  startDate: string;
  endDate: string;
  notes: string;
}): Promise<void> {
  await getApiHttpClient().post('/api/v1/cfo/capacity/supplements', body);
}

export async function removeExtraHours(supplementId: string): Promise<void> {
  await getApiHttpClient().delete(`/api/v1/cfo/capacity/supplements/${supplementId}`);
}

// The portfolio's clients, searched by the registry route; the filters carry
// the portfolio's owner when the page is on one.
export const searchClients: OptionFetcher = async ({ search, page, filters }) => {
  const res = await getApiHttpClient().get<{
    clients: ClientOption[];
    pagination: PaginationMeta;
  }>('/api/v1/cfo/clients', {
    params: { active: true, ...(search && { search }), page, ...filters },
  });
  return { items: res.data.clients, pagination: res.data.pagination };
};

export const searchPeople: OptionFetcher = async ({ search, page }) => {
  const res = await getApiHttpClient().get<{
    people: PersonOption[];
    pagination: PaginationMeta;
  }>('/api/v1/cfo/capacity/people', { params: { ...(search && { search }), page } });
  return { items: res.data.people, pagination: res.data.pagination };
};

export async function fetchExcessBill(clientId: string, month: string): Promise<ExcessBill> {
  const res = await getApiHttpClient().get<ExcessBill>('/api/v1/cfo/capacity/excess', {
    params: { clientId, month },
  });
  return res.data;
}

export async function generateExcessBill(body: GenerateExcessBillBody): Promise<ExcessBill> {
  const res = await getApiHttpClient().post<ExcessBill>('/api/v1/cfo/capacity/excess', body);
  return res.data;
}

// The platform renders the file and emails its link; nothing comes back but the id.
export async function requestExcessExport(body: ExcessExportBody): Promise<ExcessExportRequested> {
  const res = await getApiHttpClient().post<ExcessExportRequested>(
    '/api/v1/cfo/capacity/excess/exports',
    body,
  );
  return res.data;
}

// The open tasks on the client's ClickUp list; the filters name the client.
export const searchClientTasks: OptionFetcher = async ({ search, page, filters }) => {
  const res = await getApiHttpClient().get<{
    tasks: ClientTaskOption[];
    pagination: PaginationMeta;
  }>(`/api/v1/cfo/clients/${String(filters?.clientId)}/tasks`, {
    params: { ...(search && { search }), page },
  });
  return { items: res.data.tasks, pagination: res.data.pagination };
};
